import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../stores/auth-store';
import { validateQBConnection, updateLastUsedTenant } from '../services/api';

type LoadingState = 'validating' | 'success' | 'error';

export function TenantLoading() {
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();
  const [loadingState, setLoadingState] = useState<LoadingState>('validating');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { tenants, selectTenant, fetchUserTenants, isAuthenticated } = useAuthStore();

  useEffect(() => {
    // Redirect if not authenticated
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }

    if (!tenantId) {
      navigate('/companies', { replace: true });
      return;
    }

    // Verify user has access to this tenant
    const tenant = tenants.find((t) => t.id === tenantId);
    if (!tenant) {
      console.error('User does not have access to tenant:', tenantId);
      navigate('/companies', { replace: true });
      return;
    }

    // Validate QB connection
    const validate = async () => {
      try {
        setLoadingState('validating');

        const result = await validateQBConnection(tenantId);

        if (result.connected) {
          // Connection is valid - update last used and redirect to dashboard
          setLoadingState('success');

          // Update last used timestamp (fire and forget)
          updateLastUsedTenant(tenantId).catch((err) => {
            console.warn('Failed to update last used tenant:', err);
          });

          // Select the tenant in store
          selectTenant(tenantId);

          // Redirect to dashboard
          navigate('/', { replace: true });
        } else {
          // Connection failed - update tenant list and redirect to company selection
          setLoadingState('error');

          // Determine error message based on reason
          let message = 'QuickBooks connection is no longer valid.';
          switch (result.reason) {
            case 'no_tokens':
              message = 'QuickBooks is not connected for this company.';
              break;
            case 'token_refresh_failed':
              message = 'Failed to refresh QuickBooks connection. Please reconnect.';
              break;
            case 'token_revoked':
              message = 'QuickBooks connection was revoked. Please reconnect.';
              break;
          }
          setErrorMessage(message);

          // Refresh tenant list to get updated disconnectedAt
          await fetchUserTenants();

          // Redirect to company selection with error
          setTimeout(() => {
            navigate('/companies', {
              replace: true,
              state: { error: message },
            });
          }, 1500);
        }
      } catch (error) {
        console.error('Error validating QB connection:', error);
        setLoadingState('error');
        setErrorMessage('An error occurred while validating the connection.');

        // Redirect to company selection
        setTimeout(() => {
          navigate('/companies', {
            replace: true,
            state: { error: 'An error occurred while validating the connection.' },
          });
        }, 1500);
      }
    };

    validate();
  }, [tenantId, tenants, isAuthenticated, navigate, selectTenant, fetchUserTenants]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full text-center space-y-6 p-8">
        {/* QuickBooks Logo */}
        <div className="flex justify-center">
          <svg
            className="w-16 h-16"
            viewBox="0 0 64 64"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="32" cy="32" r="30" fill="#2CA01C" />
            <path
              d="M32 14C23.163 14 16 21.163 16 30C16 38.837 23.163 46 32 46C40.837 46 48 38.837 48 30C48 21.163 40.837 14 32 14ZM32 42C25.373 42 20 36.627 20 30C20 23.373 25.373 18 32 18C38.627 18 44 23.373 44 30C44 36.627 38.627 42 32 42Z"
              fill="white"
            />
            <path
              d="M36 26H34V24C34 22.895 33.105 22 32 22C30.895 22 30 22.895 30 24V26H28C26.895 26 26 26.895 26 28C26 29.105 26.895 30 28 30H30V36C30 37.105 30.895 38 32 38C33.105 38 34 37.105 34 36V30H36C37.105 30 38 29.105 38 28C38 26.895 37.105 26 36 26Z"
              fill="white"
            />
          </svg>
        </div>

        {/* Loading State */}
        {loadingState === 'validating' && (
          <>
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Connecting to QuickBooks</h2>
              <p className="mt-2 text-sm text-gray-500">Validating your connection...</p>
            </div>
          </>
        )}

        {/* Success State */}
        {loadingState === 'success' && (
          <>
            <div className="flex justify-center">
              <svg
                className="w-8 h-8 text-green-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Connected</h2>
              <p className="mt-2 text-sm text-gray-500">Redirecting to dashboard...</p>
            </div>
          </>
        )}

        {/* Error State */}
        {loadingState === 'error' && (
          <>
            <div className="flex justify-center">
              <svg
                className="w-8 h-8 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Connection Failed</h2>
              <p className="mt-2 text-sm text-red-600">{errorMessage}</p>
              <p className="mt-1 text-sm text-gray-500">Redirecting...</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
